import { CaretRight, PlusCircle } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  DropdownMenu,
  Flex,
  Heading,
  Link as RadixLink,
  Select,
  Spinner,
  Table,
  Text,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { useMemo, useState } from "react";

import { auditLogRoute } from "./route";

import { useAuditLog } from "../../../../utils/queries/auditLog";

// All task categories in order (AT1-AT10)
const ALL_TASK_CATEGORIES = [
  { id: "AT1", label: "Previous Feedback Implemented" },
  { id: "AT2", label: "Copy Errors" },
  { id: "AT3", label: "Brand Logo Usage" },
  { id: "AT4", label: "Validate SKU/Product Variants" },
  { id: "AT5", label: "Claim Language Approved" },
  { id: "AT6", label: "Brand Standard Adherence" },
  { id: "AT7", label: "Assets Match Brief Specs" },
  { id: "AT8", label: "Retailer/Channel Requirements Met" },
  { id: "AT9", label: "Required Legal Text Present" },
  { id: "AT10", label: "Required Legal Text Correct" },
];

// Task category filter options (sorted alphabetically by label)
const TASK_CATEGORY_OPTIONS = ALL_TASK_CATEGORIES.map((cat) => ({
  value: cat.id,
  label: cat.label,
})).sort((a, b) => a.label.localeCompare(b.label));

// Month filter options
const DATE_FILTER_OPTIONS = [
  { value: "2026-01", label: "January 2026" },
  { value: "2025-12", label: "December 2025" },
  { value: "2025-11", label: "November 2025" },
  { value: "2025-10", label: "October 2025" },
];

// Filter menu levels
type FilterMenuLevel = "main" | "date" | "taskCategory";

// Transform API entry to display format
interface DisplayEntry {
  id: string;
  wrikeTaskId: string;
  wrikeTaskName: string;
  wrikePermalink?: string;
  punttTicketId: string;
  activeTaskCategories: string[]; // AT IDs that are active for this entry
  humanCaughtErrors: {
    category: string;
    text: string;
  }[];
}

export default function AuditLogView() {
  const { atomicTasks, startDate, endDate, page } = auditLogRoute.useSearch();
  const navigate = auditLogRoute.useNavigate();

  const selectedTaskCategory = atomicTasks?.[0]; // Use first selected task as filter
  const selectedDateFilter = startDate ? startDate.substring(0, 7) : undefined; // Extract YYYY-MM

  const [filterMenuLevel, setFilterMenuLevel] =
    useState<FilterMenuLevel>("main");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // Fetch audit log data
  const {
    data: auditLogData,
    isLoading,
    isError,
    error,
  } = useAuditLog({
    page: page ?? 1,
    pageSize: 50,
    atomicTasks: atomicTasks,
    startDate: startDate,
    endDate: endDate,
  });

  const handleTaskCategoryChange = (value: string) => {
    const filterValue = value === "__clear__" ? undefined : value;
    navigate({
      search: (prev) => ({
        ...prev,
        atomicTasks: filterValue ? [filterValue] : undefined,
        page: 1,
      }),
      replace: true,
    });
  };

  const handleDateFilterChange = (value: string) => {
    const filterValue = value === "__clear__" ? undefined : value;
    if (filterValue) {
      // Convert YYYY-MM to date range
      const startDate = `${filterValue}-01`;
      const endDate = `${filterValue}-31`;
      navigate({
        search: (prev) => ({
          ...prev,
          startDate,
          endDate,
          page: 1,
        }),
        replace: true,
      });
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startDate: undefined,
          endDate: undefined,
          page: 1,
        }),
        replace: true,
      });
    }
  };

  const handleFilterMenuOpenChange = (open: boolean) => {
    setFilterMenuOpen(open);
    if (!open) {
      setFilterMenuLevel("main");
    }
  };

  // Transform API data to display format and group by Wrike Task + Ticket
  const groupedEntries = useMemo(() => {
    if (!auditLogData?.entries) return [];

    const groups = new Map<
      string,
      {
        wrikeTaskId: string;
        wrikeTaskName: string;
        wrikePermalink?: string;
        punttTicketId: string;
        entries: DisplayEntry[];
      }
    >();

    auditLogData.entries.forEach((entry) => {
      const ticketId = entry.stats?.ticket_ids?.[0] || entry._id;
      const key = `${entry.task_id}-${ticketId}`;

      if (!groups.has(key)) {
        groups.set(key, {
          wrikeTaskId: entry.task_id || "Unknown",
          wrikeTaskName: entry.task_name || entry.task_id || "Unknown Task",
          wrikePermalink: entry.wrike_permalink,
          punttTicketId: ticketId,
          entries: [],
        });
      }

      // Create a display entry for each row
      const displayEntry: DisplayEntry = {
        id: entry._id,
        wrikeTaskId: entry.task_id || "Unknown",
        wrikeTaskName: entry.task_name || entry.task_id || "Unknown Task",
        wrikePermalink: entry.wrike_permalink,
        punttTicketId: ticketId,
        activeTaskCategories: entry.stats?.atomic_tasks || [],
        humanCaughtErrors: (entry.stats?.missed_issues || []).map((issue) => ({
          category: issue.atomic_task,
          text: issue.comment_text,
        })),
      };

      groups.get(key)!.entries.push(displayEntry);
    });

    return Array.from(groups.values());
  }, [auditLogData]);

  if (isLoading) {
    return (
      <article
        className="flex flex-col items-center justify-center gap-6 overflow-auto px-10 py-6"
        data-testid="audit-log-page"
      >
        <Spinner size="3" />
        <Text size="3">Loading audit log...</Text>
      </article>
    );
  }

  if (isError) {
    return (
      <article
        className="flex flex-col gap-6 overflow-auto px-10 py-6"
        data-testid="audit-log-page"
      >
        <Heading as="h1" size="7" weight="bold">
          Wrike Tasks Audit Log
        </Heading>
        <Text size="3" color="red">
          Error loading audit log:{" "}
          {(error as Error)?.message || "Unknown error"}
        </Text>
      </article>
    );
  }

  const totalEntries = auditLogData?.total ?? 0;

  return (
    <article
      className="flex flex-col gap-6 overflow-auto px-10 py-6"
      data-testid="audit-log-page"
    >
      {/* Header */}
      <header>
        <Heading as="h1" size="7" weight="bold">
          Wrike Tasks Audit Log
        </Heading>
      </header>

      {/* Filters Row */}
      <Flex align="center" gap="3" wrap="wrap">
        {/* Active Date Filter Pill */}
        {selectedDateFilter && (
          <Select.Root
            value={selectedDateFilter}
            onValueChange={handleDateFilterChange}
          >
            <Select.Trigger
              variant="surface"
              className="min-w-[140px]"
              data-testid="date-filter"
            />
            <Select.Content>
              <Select.Item value="__clear__">Clear</Select.Item>
              {DATE_FILTER_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        )}

        {/* Active Task Category Filter Pill */}
        {selectedTaskCategory && (
          <Select.Root
            value={selectedTaskCategory}
            onValueChange={handleTaskCategoryChange}
          >
            <Select.Trigger
              variant="surface"
              className="min-w-[180px]"
              data-testid="task-category-filter"
            />
            <Select.Content>
              <Select.Item value="__clear__">Clear</Select.Item>
              {TASK_CATEGORY_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        )}

        {/* Multi-level Filter Dropdown */}
        <DropdownMenu.Root
          open={filterMenuOpen}
          onOpenChange={handleFilterMenuOpenChange}
        >
          <DropdownMenu.Trigger>
            <Button variant="outline" color="gray">
              <PlusCircle size={16} />
              Filter
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content>
            {filterMenuLevel === "main" && (
              <>
                <DropdownMenu.Item
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterMenuLevel("date");
                  }}
                >
                  <Flex justify="between" align="center" className="w-full">
                    Date
                    <CaretRight size={14} />
                  </Flex>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterMenuLevel("taskCategory");
                  }}
                >
                  <Flex justify="between" align="center" className="w-full">
                    Task Category
                    <CaretRight size={14} />
                  </Flex>
                </DropdownMenu.Item>
              </>
            )}

            {filterMenuLevel === "date" && (
              <>
                {DATE_FILTER_OPTIONS.map((option) => (
                  <DropdownMenu.Item
                    key={option.value}
                    onClick={() => {
                      handleDateFilterChange(option.value);
                      setFilterMenuOpen(false);
                      setFilterMenuLevel("main");
                    }}
                    className={cx({
                      "bg-puntt-accent-3": selectedDateFilter === option.value,
                    })}
                  >
                    {option.label}
                  </DropdownMenu.Item>
                ))}
              </>
            )}

            {filterMenuLevel === "taskCategory" && (
              <>
                {TASK_CATEGORY_OPTIONS.map((option) => (
                  <DropdownMenu.Item
                    key={option.value}
                    onClick={() => {
                      handleTaskCategoryChange(option.value);
                      setFilterMenuOpen(false);
                      setFilterMenuLevel("main");
                    }}
                    className={cx({
                      "bg-puntt-accent-3":
                        selectedTaskCategory === option.value,
                    })}
                  >
                    {option.label}
                  </DropdownMenu.Item>
                ))}
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {/* Results count */}
        <Text size="2" color="gray" className="ml-auto">
          {totalEntries} {totalEntries === 1 ? "entry" : "entries"} found
        </Text>
      </Flex>

      {/* Audit Log Table */}
      <Table.Root variant="surface" data-testid="audit-log-table">
        <Table.Header>
          <Table.Row className="bg-puntt-neutral-gray-3">
            <Table.ColumnHeaderCell className="min-w-[200px] font-medium">
              Wrike Task
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[180px] font-medium">
              Puntt Ticket
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[200px] font-medium">
              Task Categories
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[300px] font-medium">
              Human-Caught Errors
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {groupedEntries.map((group) => (
            <GroupedRows
              key={`${group.wrikeTaskId}-${group.punttTicketId}`}
              wrikeTaskId={group.wrikeTaskId}
              wrikeTaskName={group.wrikeTaskName}
              wrikePermalink={group.wrikePermalink}
              punttTicketId={group.punttTicketId}
              entries={group.entries}
              activeTaskCategory={selectedTaskCategory}
            />
          ))}
        </Table.Body>
      </Table.Root>

      {groupedEntries.length === 0 && (
        <Flex className="py-4 text-puntt-neutral-gray-9">
          <Text size="3">No audit log entries found.</Text>
        </Flex>
      )}
    </article>
  );
}

interface GroupedRowsProps {
  wrikeTaskId: string;
  wrikeTaskName: string;
  wrikePermalink?: string;
  punttTicketId: string;
  entries: DisplayEntry[];
  activeTaskCategory?: string;
}

function GroupedRows({
  wrikeTaskId,
  wrikeTaskName,
  wrikePermalink,
  punttTicketId,
  entries,
  activeTaskCategory,
}: GroupedRowsProps) {
  // Combine all active task categories and errors from all entries in the group
  const allActiveCategories = new Set<string>();
  const allErrors: { category: string; text: string }[] = [];

  entries.forEach((entry) => {
    entry.activeTaskCategories.forEach((cat) => allActiveCategories.add(cat));
    allErrors.push(...entry.humanCaughtErrors);
  });

  return (
    <Table.Row className="border-b border-puntt-neutral-gray-4">
      {/* Wrike Task */}
      <Table.Cell className="align-top">
        <RadixLink
          href={
            wrikePermalink || `https://www.wrike.com/open.htm?id=${wrikeTaskId}`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-puntt-accent-9 underline decoration-puntt-accent-9"
        >
          {wrikeTaskName}
        </RadixLink>
      </Table.Cell>

      {/* Puntt Ticket */}
      <Table.Cell className="align-top">
        <RadixLink asChild>
          <Link
            to="/tickets/$ticketId/view"
            params={{ ticketId: punttTicketId }}
            className="font-medium text-puntt-accent-9 underline decoration-puntt-accent-9"
          >
            {punttTicketId}
          </Link>
        </RadixLink>
      </Table.Cell>

      {/* Task Categories + Human-Caught Errors - Combined in a 2-column layout */}
      <Table.Cell colSpan={2} className="p-0">
        <div className="flex flex-col">
          {ALL_TASK_CATEGORIES.filter((category) =>
            allActiveCategories.has(category.id),
          ).map((category, index, filteredArray) => {
            const isFilteredCategory = activeTaskCategory === category.id;
            const categoryErrors = allErrors.filter(
              (error) => error.category === category.id,
            );
            const isLastRow = index === filteredArray.length - 1;

            return (
              <div
                key={category.id}
                className={cx(
                  "flex",
                  !isLastRow && "border-b border-puntt-neutral-gray-4",
                  isFilteredCategory && "bg-puntt-accent-3",
                )}
              >
                {/* Category Badge Column */}
                <div className="w-[200px] min-w-[200px] p-2">
                  <Badge variant="soft" color="gray" size="1">
                    {category.label}
                  </Badge>
                </div>

                {/* Errors Column */}
                <div className="flex-1 p-2">
                  {categoryErrors.length > 0 ? (
                    <ol className="m-0 list-inside list-decimal pl-0">
                      {categoryErrors.map((error, idx) => (
                        <li key={idx} className="text-sm">
                          <Text size="2">{error.text}</Text>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Table.Cell>
    </Table.Row>
  );
}
